import type {
  AvailabilityState,
  CrowdLevel,
  DataSnapshot,
  EvaluatedToilet,
  EvaluationContext,
  Evidence,
  FeatureKey,
  HazardEvent,
  Scenario,
  StatusObservation,
  ToiletFacility,
} from "./types";

const EARTH_RADIUS_METERS = 6_371_000;
const FACILITY_TIME_ZONE = "Asia/Tokyo";
const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};
const facilityClockFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: FACILITY_TIME_ZONE,
  weekday: "short",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});
const STATUS_PENALTY: Record<AvailabilityState, number> = {
  available_confirmed: 0,
  available_likely: 4,
  limited: 12,
  unknown: 18,
  likely_unavailable: 35,
  unavailable_confirmed: 1_000,
};
const QUEUE_PENALTY: Record<CrowdLevel, number> = {
  normal: 0,
  busy: 8,
  very_busy: 18,
  unknown: 5,
};

type FacilityClosureSignal = {
  marker: string;
  fieldLabel: string;
};

const JAPANESE_CURRENT_CLOSURE =
  /(休業中|閉館中|閉鎖中|廃止済み|廃止済)/u;
const JAPANESE_DECORATED_CLOSURE =
  /[【\[（(]\s*(休業中|閉館中?|閉鎖中?|廃止済み?|廃止)\s*[】\]）)]/u;
const JAPANESE_NAME_CLOSURE =
  /(?:^|[\s:：・\-—／/])(休業中|閉館中?|閉鎖中?|廃止済み?|廃止)(?=$|[\s:：・\-—／/])/u;
const ENGLISH_DECORATED_CLOSURE =
  /[[(]\s*((?:temporarily\s+|permanently\s+)?closed)\s*[\])]/iu;
const ENGLISH_CURRENT_CLOSURE =
  /\b((?:currently|temporarily|permanently)\s+closed|closed\s+until\s+further\s+notice)\b/iu;

const normalizeFacilityText = (value: string | undefined) =>
  value?.normalize("NFKC").replace(/\s+/gu, " ").trim() ?? "";

const closureMarkerInName = (value: string, language: "ja" | "en") => {
  if (!value) return undefined;

  if (language === "ja") {
    const decorated = value.match(JAPANESE_DECORATED_CLOSURE);
    if (decorated?.[1]) return decorated[1];
    const current = value.match(JAPANESE_CURRENT_CLOSURE);
    if (current?.[1]) return current[1];
    const separated = value.match(JAPANESE_NAME_CLOSURE);
    return separated?.[1];
  }

  const decorated = value.match(ENGLISH_DECORATED_CLOSURE);
  if (decorated?.[1]) return decorated[1];
  const current = value.match(ENGLISH_CURRENT_CLOSURE);
  if (current?.[1]) return current[1];
  const exactOrSuffix = value.match(
    /(?:^|[\s:：\-—/])((?:temporarily\s+|permanently\s+)?closed)$/iu,
  );
  if (exactOrSuffix?.[1]) return exactOrSuffix[1];
  return value.match(/^CLOSED(?=\s|[:：\-—/])/u)?.[0];
};

const closureMarkerInStructuredStatus = (value: string) => {
  if (!value) return undefined;

  const decorated = value.match(JAPANESE_DECORATED_CLOSURE);
  if (decorated?.[1]) return decorated[1];
  const current = value.match(JAPANESE_CURRENT_CLOSURE);
  if (current?.[1]) return current[1];
  const explicitJapanese = value.match(
    /(?:現在|ただいま)[^。\n]{0,4}(休業中|閉館中|閉鎖中|休業しています|閉館しています|閉鎖しています|休業しております|閉館しております|閉鎖しております)/u,
  );
  if (explicitJapanese?.[1]) return explicitJapanese[1];
  const indefiniteJapanese = value.match(
    /当面(?:の間)?(?:は)?(休業|閉館|閉鎖)(?:します|いたします)?(?=$|[。,.、])/u,
  );
  if (indefiniteJapanese?.[1]) return indefiniteJapanese[1];
  const exactJapanese = value.match(/^(休業|閉館|閉鎖|廃止)$/u);
  if (exactJapanese?.[1]) return exactJapanese[1];

  const explicitEnglish = value.match(ENGLISH_CURRENT_CLOSURE);
  if (explicitEnglish?.[1]) return explicitEnglish[1];
  return value.match(/^closed$/iu)?.[0];
};

/**
 * Detect only explicit, current closure status. Recurring schedule wording such as
 * 「休館日」 or "closed on Mondays" intentionally remains outside this gate.
 */
export function getFacilityClosureSignal(
  toilet: ToiletFacility,
): FacilityClosureSignal | undefined {
  const fields = [
    {
      value: normalizeFacilityText(toilet.name),
      fieldLabel: "施設名",
      detect: (value: string) => closureMarkerInName(value, "ja"),
    },
    {
      value: normalizeFacilityText(toilet.nameEn),
      fieldLabel: "英語施設名",
      detect: (value: string) => closureMarkerInName(value, "en"),
    },
    {
      value: normalizeFacilityText(toilet.openingLabel),
      fieldLabel: "利用時間情報",
      detect: closureMarkerInStructuredStatus,
    },
    {
      value: normalizeFacilityText(toilet.openingNote),
      fieldLabel: "利用条件情報",
      detect: closureMarkerInStructuredStatus,
    },
  ];

  for (const field of fields) {
    const marker = field.detect(field.value);
    if (marker) return { marker, fieldLabel: field.fieldLabel };
  }
  return undefined;
}

const toRadians = (value: number) => (value * Math.PI) / 180;

export function distanceMeters(
  from: { latitude: number; longitude: number },
  to: { latitude: number; longitude: number },
) {
  const dLat = toRadians(to.latitude - from.latitude);
  const dLng = toRadians(to.longitude - from.longitude);
  const lat1 = toRadians(from.latitude);
  const lat2 = toRadians(to.latitude);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return Math.round(EARTH_RADIUS_METERS * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
}

const facilityClock = (date: Date) => {
  const parts = new Map(
    facilityClockFormatter.formatToParts(date).map(({ type, value }) => [type, value]),
  );
  const day = WEEKDAY_INDEX[parts.get("weekday") ?? ""];
  const hour = Number(parts.get("hour"));
  const minute = Number(parts.get("minute"));

  if (day === undefined || !Number.isInteger(hour) || !Number.isInteger(minute)) {
    throw new RangeError("Could not resolve the facility clock in Asia/Tokyo");
  }

  return { day, minute: hour * 60 + minute };
};

const timeToMinutes = (time: string) => {
  const [hours = 0, minutes = 0] = time.split(":").map(Number);
  return hours * 60 + minutes;
};

export function isFacilityOpen(toilet: ToiletFacility, at: Date) {
  const { day, minute } = facilityClock(at);
  return toilet.openingPeriods.some((period) => {
    if (!period.days.includes(day)) return false;
    const open = timeToMinutes(period.open);
    const close = timeToMinutes(period.close);
    if (close >= open) return minute >= open && minute <= close;
    return minute >= open || minute <= close;
  });
}

export function resolveScenarioTime(
  snapshot: DataSnapshot,
  scenario: Scenario,
  futureMinutes: number,
  customTime?: string,
) {
  if (customTime) return new Date(customTime);
  const base =
    scenario === "normal"
      ? new Date()
      : new Date(snapshot.demoClock[scenario as keyof DataSnapshot["demoClock"]]);
  return new Date(base.getTime() + futureMinutes * 60_000);
}

const activeObservations = (
  observations: StatusObservation[],
  toiletId: string,
  at: Date,
) =>
  observations.filter(
    (observation) =>
      observation.toiletId === toiletId &&
      new Date(observation.observedAt) <= at &&
      new Date(observation.validUntil) >= at,
  );

const observationTimestamp = (observation: StatusObservation) => {
  const timestamp = Date.parse(observation.observedAt);
  return Number.isFinite(timestamp) ? timestamp : Number.NEGATIVE_INFINITY;
};

const latestObservation = (
  observations: StatusObservation[],
  matches: (observation: StatusObservation) => boolean,
) =>
  observations.reduce<StatusObservation | undefined>((latest, observation) => {
    if (!matches(observation)) return latest;
    if (
      !latest ||
      observationTimestamp(observation) > observationTimestamp(latest)
    ) {
      return observation;
    }
    return latest;
  }, undefined);

const appliesToScenario = (
  hazard: HazardEvent,
  context: EvaluationContext,
  toiletId: string,
) => {
  const from = new Date(hazard.validFrom).getTime();
  const graceMinutes = hazard.type === "event_crowding" ? 30 : 0;
  const to = new Date(hazard.validTo).getTime() + graceMinutes * 60_000;
  const appliesToCurrentSituation = context.activeHazardIds
    ? context.activeHazardIds.includes(hazard.id)
    : hazard.scenarios.includes(context.scenario);
  return (
    appliesToCurrentSituation &&
    hazard.affectedToiletIds.includes(toiletId) &&
    context.at.getTime() >= from &&
    context.at.getTime() <= to
  );
};

const featureMatches = (toilet: ToiletFacility, key: FeatureKey, required: boolean) =>
  !required || toilet.features[key] === true;

const formatFreshness = (date: string | undefined, at: Date) => {
  if (!date) return "現場確認なし";
  const minutes = Math.max(0, Math.round((at.getTime() - new Date(date).getTime()) / 60_000));
  if (minutes < 1) return "1分以内に確認";
  if (minutes < 60) return `${minutes}分前に確認`;
  return `${Math.round(minutes / 60)}時間前に確認`;
};

function stateFromEvidence(
  toilet: ToiletFacility,
  context: EvaluationContext,
  hazards: HazardEvent[],
): {
  state: AvailabilityState;
  crowdLevel: CrowdLevel;
  reasons: string[];
  evidence: Evidence[];
  lastConfirmedAt?: string;
  queueLabel: string;
} {
  const observations = activeObservations(context.observations, toilet.id, context.at);
  const operatorObservation = latestObservation(
    observations,
    (item) =>
      item.type === "operator" &&
      (item.status === "available" || item.status === "unavailable"),
  );
  const officialObservation = latestObservation(
    observations,
    (item) =>
      item.type === "official" &&
      (item.status === "available" || item.status === "unavailable"),
  );
  const operatorUnavailable =
    operatorObservation?.status === "unavailable" ? operatorObservation : undefined;
  const operatorAvailable =
    operatorObservation?.status === "available" ? operatorObservation : undefined;
  const officialUnavailable =
    officialObservation?.status === "unavailable" ? officialObservation : undefined;
  const officialAvailable =
    officialObservation?.status === "available" ? officialObservation : undefined;
  const communityUnavailable = observations.filter(
    (item) => item.type === "community" && item.status === "unavailable",
  );
  const communityAvailable = observations.filter(
    (item) => item.type === "community" && item.status === "available",
  );
  const queues = observations.filter((item) => item.status === "queue");
  const activeHazards = hazards.filter((hazard) =>
    appliesToScenario(hazard, context, toilet.id),
  );
  const evidence: Evidence[] = [];
  const reasons: string[] = [];
  let state: AvailabilityState = "available_likely";
  let crowdLevel: CrowdLevel = "normal";
  let queueLabel = "混雑情報なし";
  let lastConfirmedAt: string | undefined;
  const facilityClosureSignal = getFacilityClosureSignal(toilet);

  const addObservation = (observation: StatusObservation, label: string, detail: string) => {
    evidence.push({
      id: observation.id,
      kind:
        observation.type === "community" ? "community_observation" : "official_observation",
      label,
      detail,
      sourceId: observation.sourceId,
      observedAt: observation.observedAt,
    });
  };

  if (facilityClosureSignal) {
    state = "unavailable_confirmed";
    reasons.push(
      `${facilityClosureSignal.fieldLabel}に「${facilityClosureSignal.marker}」の記載`,
    );
    evidence.push({
      id: `${toilet.id}-facility-closure-signal`,
      kind: "static_baseline",
      label: "施設の休業・閉鎖情報",
      detail: `${facilityClosureSignal.fieldLabel}に「${facilityClosureSignal.marker}」と記載されています`,
      sourceId: toilet.sourceIds[0] ?? "facility-baseline",
      observedAt: toilet.sourceUpdatedAt,
      limitation:
        "営業再開が確認され、施設情報が更新されるまで案内候補から除外します。",
    });
  } else if (operatorUnavailable) {
    state = "unavailable_confirmed";
    reasons.push("施設運営者が利用不可を確認");
    addObservation(operatorUnavailable, "施設運営者の確認", "利用不可が確認されています");
  } else if (officialUnavailable) {
    state = "unavailable_confirmed";
    reasons.push("公式情報で閉鎖を確認");
    addObservation(officialUnavailable, "公式閉鎖情報", "施設の閉鎖が確認されています");
  } else if (activeHazards.some((hazard) => hazard.type === "sewer_restriction")) {
    state = "likely_unavailable";
    reasons.push("下水道使用制限の影響範囲");
  } else if (operatorAvailable) {
    state = "available_confirmed";
    lastConfirmedAt = operatorAvailable.observedAt;
    reasons.push("施設運営者が利用可能と確認");
    addObservation(operatorAvailable, "施設運営者の確認", "利用可能が確認されています");
  } else if (officialAvailable) {
    state = "available_confirmed";
    lastConfirmedAt = officialAvailable.observedAt;
    reasons.push("公式情報で利用可能と確認");
    addObservation(officialAvailable, "公式利用情報", "利用可能が確認されています");
  } else if (communityUnavailable.length >= 2) {
    state = "likely_unavailable";
    reasons.push(`短時間に${communityUnavailable.length}件の利用不可報告`);
    communityUnavailable.forEach((item) =>
      addObservation(item, "複数の利用者報告", "利用できなかったとの報告"),
    );
  } else if (communityAvailable.length >= 2) {
    state = "available_confirmed";
    lastConfirmedAt = latestObservation(communityAvailable, () => true)?.observedAt;
    reasons.push(`${communityAvailable.length}件の直近利用報告`);
    communityAvailable.forEach((item) =>
      addObservation(item, "利用者による確認", "使えたとの直近報告"),
    );
  } else if (activeHazards.some((hazard) => hazard.type === "water_outage")) {
    const outageHazard = activeHazards.find(
      (hazard) => hazard.type === "water_outage",
    );
    state = "likely_unavailable";
    reasons.push(
      outageHazard?.isSimulation
        ? "訓練の断水影響区域内・施設設備は要確認"
        : "断水影響区域内・施設設備は要確認",
    );
  } else if (activeHazards.some((hazard) => hazard.type === "earthquake_inspection")) {
    const earthquakeHazard = activeHazards.find(
      (hazard) => hazard.type === "earthquake_inspection",
    );
    state = "unknown";
    reasons.push(
      earthquakeHazard?.isSimulation
        ? "強い揺れの後を想定・給排水設備の点検待ち"
        : "強い揺れを観測・給排水設備の点検待ち",
    );
  } else if (communityUnavailable.length === 1) {
    state = "unknown";
    reasons.push("利用できなかったとの単独報告・追加確認が必要");
    addObservation(
      communityUnavailable[0]!,
      "単独の利用者報告",
      "1件だけでは利用不可と断定しません",
    );
  }

  const eventHazard = activeHazards.find((hazard) => hazard.type === "event_crowding");
  if (
    eventHazard &&
    !["unavailable_confirmed", "likely_unavailable", "unknown"].includes(state)
  ) {
    const minutesFromEnd =
      (context.at.getTime() - new Date(eventHazard.validTo).getTime()) / 60_000;
    state = "limited";
    crowdLevel = minutesFromEnd >= -30 ? "very_busy" : "busy";
    queueLabel =
      crowdLevel === "very_busy" ? "非常に混雑する見込み" : "混雑する見込み";
    reasons.push("イベントの同時開催数・利用ホールからの混雑予測");
  }

  if (queues.length > 0) {
    const hasLongQueue = queues.some((item) => item.queueBand === "over_15_min");
    crowdLevel = hasLongQueue ? "very_busy" : "busy";
    queueLabel = hasLongQueue ? "15分以上の行列報告" : "行列報告あり";
    if (!["unavailable_confirmed", "likely_unavailable", "unknown"].includes(state)) {
      state = "limited";
    }
    reasons.push(queueLabel);
  }

  const isOpen = isFacilityOpen(toilet, context.at);
  const hasPublishedSchedule = toilet.openingPeriods.length > 0;
  if (
    hasPublishedSchedule &&
    !isOpen &&
    state !== "unavailable_confirmed" &&
    !lastConfirmedAt
  ) {
    state = "likely_unavailable";
    reasons.unshift("公開時間外の見込み");
  } else if (
    !lastConfirmedAt &&
    ["available_likely", "limited"].includes(state) &&
    toilet.publicAccess === "facility_users_only"
  ) {
    state = "unknown";
    reasons.unshift("施設利用者向け・入館条件の確認が必要");
  } else if (
    !lastConfirmedAt &&
    ["available_likely", "limited"].includes(state) &&
    toilet.publicAccess === "unknown"
  ) {
    state = "unknown";
    reasons.unshift("一般利用の条件が不明・施設への確認が必要");
  } else if (
    toilet.publicAccess === "event_only" &&
    context.scenario === "normal" &&
    state !== "unavailable_confirmed" &&
    !lastConfirmedAt
  ) {
    state = "unknown";
    reasons.unshift("施設内・入館条件が未確認");
  } else if (reasons.length === 0) {
    reasons.push("公開時間内・既知の障害情報なし");
  }

  for (const hazard of activeHazards) {
    evidence.push({
      id: hazard.id,
      kind: hazard.isSimulation ? "simulation" : "official_observation",
      label: hazard.title,
      detail: hazard.description,
      sourceId: hazard.sourceId,
      observedAt: hazard.sourceUpdatedAt,
      limitation: hazard.limitation,
    });
  }

  for (const sourceId of toilet.sourceIds) {
    evidence.push({
      id: `${toilet.id}-${sourceId}`,
      kind: "static_baseline",
      label: "施設・設備の公開データ",
      detail: `${toilet.openingLabel}・設備情報`,
      sourceId,
      observedAt: toilet.sourceUpdatedAt,
      limitation: "静的データだけでは現在の利用可否を断定できません。",
    });
  }

  if (!context.online && state === "available_confirmed") {
    state = "available_likely";
    reasons.unshift("オフライン保存時点では利用可能");
  }

  return {
    state,
    crowdLevel,
    reasons,
    evidence,
    lastConfirmedAt,
    queueLabel,
  };
}

function evaluateAllToilets(
  snapshot: DataSnapshot,
  context: EvaluationContext,
): EvaluatedToilet[] {
  return snapshot.toilets
    .filter(
      (toilet) =>
        featureMatches(toilet, "wheelchair", context.needs.wheelchair) &&
        featureMatches(toilet, "ostomate", context.needs.ostomate) &&
        featureMatches(toilet, "baby_changing", context.needs.baby_changing),
    )
    .map((toilet) => {
      const distance = distanceMeters(context.origin, toilet.coordinates);
      const walkingMinutes = Math.max(1, Math.ceil(distance / 72));
      const result = stateFromEvidence(toilet, context, snapshot.hazards);
      const isOpen = isFacilityOpen(toilet, context.at);
      const freshnessLabel = formatFreshness(result.lastConfirmedAt, context.at);
      const stalePenalty = result.lastConfirmedAt ? 0 : 3;
      const expectedCost =
        walkingMinutes +
        STATUS_PENALTY[result.state] +
        QUEUE_PENALTY[result.crowdLevel] +
        stalePenalty;
      return {
        ...toilet,
        ...result,
        distanceMeters: distance,
        walkingMinutes,
        expectedCost,
        isOpen,
        freshnessLabel,
      };
    })
    .sort(
      (left, right) =>
        left.expectedCost - right.expectedCost || left.distanceMeters - right.distanceMeters,
    );
}

/** Includes unavailable facilities so an operator can review and correct their status. */
export function evaluateToiletsForOperations(
  snapshot: DataSnapshot,
  context: EvaluationContext,
): EvaluatedToilet[] {
  return evaluateAllToilets(snapshot, context);
}

/** Returns only facilities that are safe to present as general-user destinations. */
export function evaluateToilets(
  snapshot: DataSnapshot,
  context: EvaluationContext,
): EvaluatedToilet[] {
  return evaluateAllToilets(snapshot, context).filter(
    (toilet) =>
      toilet.state !== "unavailable_confirmed" &&
      (!context.needs.openNow || toilet.isOpen),
  );
}

export function getActiveHazards(
  snapshot: DataSnapshot,
  scenario: Scenario,
  at = new Date(),
): HazardEvent[] {
  return getCurrentlyActiveHazards(snapshot, at).filter((hazard) =>
    hazard.scenarios.includes(scenario),
  );
}

export function getCurrentlyActiveHazards(
  snapshot: DataSnapshot,
  at = new Date(),
): HazardEvent[] {
  const timestamp = at.getTime();
  const eventGracePeriodMs = 30 * 60_000;

  return snapshot.hazards.filter((hazard) => {
    const startsAt = new Date(hazard.validFrom).getTime();
    const endsAt = new Date(hazard.validTo).getTime();
    const gracePeriod =
      hazard.type === "event_crowding" ? eventGracePeriodMs : 0;

    return timestamp >= startsAt && timestamp <= endsAt + gracePeriod;
  });
}
